import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { head } from 'lodash-es';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { Group } from '../../models/group.model';
import { GroupsService } from '../../services/groups.service';
import {
  VIRTUAL_ROOT_NODE,
  VIRTUAL_ROOT_NODE_ID,
} from '../../views/media/media.component';
import { BaseDialog } from '../dialog/base-dialog';
import { MoveToFolderDialogData } from './services/move-to-folder-dialog.service';

@Component({
  selector: 'app-move-to-folder-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogClose,
    MatDialogTitle,
    MatIcon,
    MatIconButton,
    MatProgressSpinner,
    MatTooltip,
  ],
  templateUrl: './move-to-folder-dialog.component.html',
  styleUrl: './move-to-folder-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveToFolderDialogComponent extends BaseDialog<MoveToFolderDialogData> {
  private readonly _dialogRef =
    inject<MatDialogRef<MoveToFolderDialogComponent>>(MatDialogRef);
  private readonly _groupService = inject(GroupsService);

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected readonly selection = new SelectionModel<Group>(false);

  protected readonly currentlySelected = toSignal(
    this.selection.changed.pipe(map((changes) => changes.source.selected)),
    { initialValue: [] }
  );

  protected readonly currentlySelectedIds = computed(() =>
    this.currentlySelected().map((value) => value._id)
  );

  private readonly _groupList = toSignal(
    this._groupService.getList(this.data.context),
    {
      initialValue: [],
    },
  );

  private readonly _actualGroupList = computed(() => [
    VIRTUAL_ROOT_NODE,
    ...this._groupList(),
  ]);

  protected readonly stack = signal<Group[]>([]);

  protected readonly currentNodeId = signal(this.data.currentFolderId);

  protected readonly selectedNode = computed(() => this.stack().at(-1) ?? null);

  protected readonly selectedNodeId = computed(() => this.selectedNode()?._id ?? null);

  protected readonly groups = computed(() =>
    this.selectedNode() === null
      ? [VIRTUAL_ROOT_NODE]
      : this._actualGroupList().filter(
          (value) =>
            value.parentId ===
              (this.selectedNodeId() === VIRTUAL_ROOT_NODE_ID
                ? null
                : this.selectedNodeId()) && value._id !== VIRTUAL_ROOT_NODE_ID
        )
  );

  protected readonly canSave = computed(() => {
    const current = this.currentNodeId();
    const target = this.selectedNodeId();

    if (this.data.type === 'file') {
      const currentlySelected = this.currentlySelectedIds();

      return (
        (current === target &&
          currentlySelected.length > 0 &&
          !currentlySelected.includes(target!)) ||
        current !== target
      );
    }

    return current != target && !this.data.sourceIds.includes(target!);
  });

  isFolderDisabled(node: Group) {
    if (this.data.type === 'folder') {
      return this.data.sourceIds.includes(node._id);
    }

    return false;
  }

  selectFolder(node: Group) {
    if (this.currentNodeId() === node._id) {
      return;
    }

    this.selection.toggle(node);
  }

  navigateNext(node: Group) {
    if (this.isFolderDisabled(node)) {
      return;
    }

    this.stack.update((currentState) => [...currentState, node]);

    this.selection.clear();

    this.selectFolder(node);
  }

  navigatePrevious() {
    this.stack.update((currentState) => currentState.slice(0, -1));

    const node = this.selectedNode();

    this.selection.clear();

    if (node) {
      this.selectFolder(node);
    }
  }

  override handleConfirm(): void {
    this.submitted.set(true);

    const callback$ = this.data.onConfirm(head(this.selection.selected));

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(1000).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => this._dialogRef.close(true)),
        take(1)
      )
      .subscribe();

    this._dialogRef.close(true);
  }
}
